import React from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import TierApprovalDialog from "../components/TierApprovalDialog";
import { KeyRound, Clock, Mail, ShieldCheck, Send, Key, AlertTriangle, Copy, CheckCircle2, Download, Users, Compass, Check, X, Play, Facebook, Instagram, Sparkles, Hash } from "lucide-react";

export default function Settings() {
  const { author } = useAuth();
  const [settings, setSettings] = React.useState(null);
  const [pending, setPending] = React.useState({ recipient_email: "", summary_time: "" });
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [sampleText, setSampleText] = React.useState("");
  const [handle, setHandle] = React.useState("@anonymous");
  const [recoveryCodes, setRecoveryCodes] = React.useState(null);
  const [rgOpen, setRgOpen] = React.useState(false);
  const [pwd, setPwd] = React.useState("");
  const [rgBusy, setRgBusy] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  // 2FA reveal / rotate
  const [totpOpen, setTotpOpen] = React.useState(false);
  const [totpRotate, setTotpRotate] = React.useState(false);
  const [totpPwd, setTotpPwd] = React.useState("");
  const [totpBusy, setTotpBusy] = React.useState(false);
  const [totpInfo, setTotpInfo] = React.useState(null);
  const [totpCopied, setTotpCopied] = React.useState(false);

  // Tier A reset (forgot passphrase)
  const [tarOpen, setTarOpen] = React.useState(false);
  const [tarPwd, setTarPwd] = React.useState("");
  const [tarTotp, setTarTotp] = React.useState("");
  const [tarNew, setTarNew] = React.useState("");
  const [tarBusy, setTarBusy] = React.useState(false);

  // Autonomy + Discovery state
  const [autonomy, setAutonomy] = React.useState(null);
  const [discovery, setDiscovery] = React.useState(null);
  const [autonomyAction, setAutonomyAction] = React.useState(null);   // {type, payload, title}
  const [newHandle, setNewHandle] = React.useState("");
  const [newHandleLabel, setNewHandleLabel] = React.useState("");
  const [newHashtag, setNewHashtag] = React.useState("");
  const [runningNow, setRunningNow] = React.useState(false);

  // Per-platform hashtag defaults — pre-fill Compose drafts.
  const [hashtagDefaults, setHashtagDefaults] = React.useState(null);
  const [hashtagInputs, setHashtagInputs] = React.useState({});
  const [hashtagBusy, setHashtagBusy] = React.useState(false);

  // Meta (FB/IG) credentials — stored in DB, not .env
  const [metaCfg, setMetaCfg] = React.useState(null);
  const [metaForm, setMetaForm] = React.useState({
    app_id: "", app_secret: "", page_id: "", page_access_token: "", ig_business_account_id: "",
  });
  const [metaBusy, setMetaBusy] = React.useState(false);

  // LLM API keys (direct Google / Anthropic billing, stored in DB)
  const [llmCfg, setLlmCfg] = React.useState(null);
  const [llmForm, setLlmForm] = React.useState({ gemini_api_key: "", claude_api_key: "" });
  const [llmBusy, setLlmBusy] = React.useState(false);

  const loadLlmCfg = async () => {
    try {
      const { data } = await api.get("/llm-keys/config");
      setLlmCfg(data);
    } catch (_e) { /* ignore */ }
  };
  const saveLlmCfg = async () => {
    setLlmBusy(true);
    try {
      const payload = {};
      if (llmForm.gemini_api_key.trim()) payload.gemini_api_key = llmForm.gemini_api_key.trim();
      if (llmForm.claude_api_key.trim()) payload.claude_api_key = llmForm.claude_api_key.trim();
      if (Object.keys(payload).length === 0) {
        toast.error("Enter at least one key.");
        setLlmBusy(false); return;
      }
      await api.put("/llm-keys/config", payload);
      toast.success("LLM API keys saved.");
      setLlmForm({ gemini_api_key: "", claude_api_key: "" });
      await loadLlmCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save keys.");
    } finally { setLlmBusy(false); }
  };

  const loadMetaCfg = async () => {
    try {
      const { data } = await api.get("/platforms/meta/config");
      setMetaCfg(data);
      // Pre-fill the visible (non-secret) fields from the current DB state.
      setMetaForm(prev => ({
        ...prev,
        app_id: data.app_id || "",
        page_id: data.page_id || "",
        ig_business_account_id: data.ig_business_account_id || "",
      }));
    } catch (e) { /* ignore — endpoint may not exist on old deploys */ }
  };

  const saveMetaCfg = async () => {
    setMetaBusy(true);
    try {
      // Only send fields the user actually typed into. Empty secret fields
      // mean "leave existing value", NOT "clear it".
      const payload = {
        app_id: metaForm.app_id,
        page_id: metaForm.page_id,
        ig_business_account_id: metaForm.ig_business_account_id,
      };
      if (metaForm.app_secret.trim()) payload.app_secret = metaForm.app_secret.trim();
      if (metaForm.page_access_token.trim()) payload.page_access_token = metaForm.page_access_token.trim();
      await api.put("/platforms/meta/config", payload);
      toast.success("Meta credentials saved.");
      // Clear the secret input fields so they don't linger on screen.
      setMetaForm(prev => ({ ...prev, app_secret: "", page_access_token: "" }));
      await loadMetaCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save Meta credentials.");
    } finally {
      setMetaBusy(false);
    }
  };

  const loadSettings = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setPending({ recipient_email: data.recipient_email || "", summary_time: data.summary_time || "08:00" });
  };
  const loadAutonomy = async () => {
    const { data } = await api.get("/autonomy");
    setAutonomy(data);
  };
  const loadDiscovery = async () => {
    const { data } = await api.get("/discovery");
    setDiscovery(data);
  };
  const loadHashtagDefaults = async () => {
    try {
      const { data } = await api.get("/hashtags/defaults");
      setHashtagDefaults(data.platforms || {});
      // Keep a per-platform draft string for the textarea input.
      const drafts = {};
      for (const [p, tags] of Object.entries(data.platforms || {})) {
        drafts[p] = tags.join(" ");
      }
      setHashtagInputs(drafts);
    } catch { /* non-fatal */ }
  };
  React.useEffect(() => {
    loadSettings();
    loadAutonomy();
    loadDiscovery();
    loadHashtagDefaults();
    loadMetaCfg();
    loadLlmCfg();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async (passphrase) => {
    await api.put("/settings", {
      recipient_email: pending.recipient_email,
      summary_time: pending.summary_time,
      passphrase,
    });
    toast.success("Delivery settings saved.");
    await loadSettings();
  };

  const [sendResult, setSendResult] = React.useState(null); // {sent, reason}

  const sendNow = async () => {
    setSendResult(null);
    try {
      const { data } = await api.post("/summary/send-now");
      setSendResult(data);
      if (data.sent) toast.success("Test summary sent. Check your inbox.");
      else toast.error("Email send failed — see red panel below for details.", { duration: 10000 });
    } catch (e) {
      setSendResult({ sent: false, reason: e?.response?.data?.detail || "Request failed." });
      toast.error(e?.response?.data?.detail || "Failed.", { duration: 10000 });
    }
  };

  const generateCodes = async () => {
    if (!pwd) return toast.error("Password required to generate recovery codes.");
    setRgBusy(true);
    try {
      const { data } = await api.post("/auth/recovery/generate", { password: pwd });
      setRecoveryCodes(data.codes);
      setRgOpen(false);
      setPwd("");
      toast.success("10 recovery codes generated. Save them somewhere safe.");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally { setRgBusy(false); }
  };

  const copyCodes = async () => {
    const text = recoveryCodes.join("\n");
    let ok = false;
    // Try modern clipboard API first.
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (_e) {
      ok = false;
    }
    // Fallback: hidden textarea + execCommand (works inside sandboxed iframes).
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch (_e2) {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      toast.success("Codes copied.");
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error("Clipboard blocked by the browser. Use 'Download .txt' or select the codes and copy manually.");
    }
  };

  const revealTotp = async () => {
    if (!totpPwd) return toast.error("Password required.");
    setTotpBusy(true);
    try {
      const { data } = await api.post("/auth/totp/reveal",
        { password: totpPwd, rotate: totpRotate });
      setTotpInfo(data);
      setTotpOpen(false);
      setTotpPwd("");
      toast.success(data.rotated
        ? "New 2FA secret issued. Re-add it to your authenticator now."
        : "Here's your 2FA secret — add it to an authenticator app.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reveal failed.");
    } finally { setTotpBusy(false); }
  };

  const copyTotpSecret = async () => {
    const text = totpInfo?.secret || "";
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "absolute"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setTotpCopied(true);
      toast.success("Secret copied.");
      setTimeout(() => setTotpCopied(false), 2500);
    } catch {
      toast.error("Clipboard blocked. Select the secret and copy manually.");
    }
  };

  const resetTierA = async () => {
    if (!tarPwd || !tarTotp || !tarNew) return toast.error("All fields required.");
    if (tarNew.length < 6) return toast.error("New passphrase must be at least 6 characters.");
    setTarBusy(true);
    try {
      await api.post("/auth/tier-a/reset", {
        password: tarPwd, totp_code: tarTotp, new_passphrase: tarNew });
      toast.success("Tier A passphrase reset. Use the new one from now on.");
      setTarOpen(false);
      setTarPwd(""); setTarTotp(""); setTarNew("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally { setTarBusy(false); }
  };

  const downloadCodes = () => {
    const text = [
      "Elle Sanctuary — Recovery Codes",
      `Generated ${new Date().toISOString()}`,
      "Keep these somewhere only you can reach. Each code is single-use.",
      "",
      ...recoveryCodes,
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `elle-sanctuary-recovery-codes-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Recovery codes downloaded.");
  };

  const runFilter = async () => {
    if (!sampleText.trim()) return;
    const { data } = await api.post("/replies/simulate",
      { platform: "twitter", author_handle: handle, text: sampleText });
    toast.success(`Classified as "${data.classification}". ${data.stored ? "Stored in inbox." : "Blocked silently — metadata only."}`);
    setSampleText("");
  };

  const makeProposal = async () => {
    const obs = "Bluesky engagement increased 2x when posting between 19:00–21:00 UK time across 21 days.";
    await api.post("/approvals/draft", { observation: obs });
    toast.success("Trend agent drafted a proposal — see Approvals.");
  };

  // ---- Autonomy actions (all Tier A) ----
  const runAutonomy = async (passphrase, payload) => {
    const { type, data: body } = payload;
    try {
      if (type === "toggle-pause") {
        await api.put("/autonomy", { passphrase, paused: body.paused });
        toast.success(body.paused ? "Autonomous follows paused." : "Autonomous follows resumed.");
      } else if (type === "set-cap") {
        await api.put("/autonomy", { passphrase, follow_cap_per_week: body.cap });
        toast.success(`Weekly follow cap set to ${body.cap}.`);
      } else if (type === "verify") {
        await api.put(`/autonomy/shortlist/${encodeURIComponent(body.handle)}/verify`,
          { passphrase, verified: body.verified });
        toast.success(body.verified ? "Verified — eligible Monday." : "Un-verified.");
      } else if (type === "remove") {
        await api.delete(`/autonomy/shortlist/${encodeURIComponent(body.handle)}`,
          { data: { passphrase } });
        toast.success(`${body.handle} removed from shortlist.`);
      } else if (type === "add-handle") {
        await api.post("/autonomy/shortlist", {
          passphrase, handle: body.handle, label: body.label || body.handle });
        toast.success(`Added ${body.handle}. Verify it before it's followed.`);
        setNewHandle(""); setNewHandleLabel("");
      } else if (type === "discovery-add") {
        await api.put("/discovery", { passphrase, add_hashtag: body.tag });
        toast.success(`Added ${body.tag} to discovery.`);
        setNewHashtag("");
      } else if (type === "discovery-remove") {
        await api.put("/discovery", { passphrase, remove_hashtag: body.tag });
        toast.success(`Removed ${body.tag}.`);
      } else if (type === "discovery-toggle") {
        await api.put("/discovery", { passphrase, enabled: body.enabled });
        toast.success(body.enabled ? "Discovery enabled." : "Discovery disabled.");
      }
      await Promise.all([loadAutonomy(), loadDiscovery()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
      throw e;
    }
  };

  const askTierA = (type, data, title) => setAutonomyAction({ type, data, title });

  const runAutonomyNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await api.post("/autonomy/run-now");
      const s = data.summary || {};
      if (s.paused) {
        toast.info("Autonomy is paused — nothing changed.");
      } else {
        toast.success(
          `Run complete · +${s.followed || 0} followed · ${s.unfollowed || 0} unfollowed · ${s.skipped_unverified || 0} unverified skipped.`
        );
      }
      await loadAutonomy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed.");
    } finally { setRunningNow(false); }
  };

  const dirty = settings && (
    settings.recipient_email !== pending.recipient_email ||
    settings.summary_time !== pending.summary_time
  );

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <div className="overline">Settings</div>
        <h1 className="font-display text-4xl mt-2">Your study.</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Account */}
        <section className="elle-surface p-6 rounded-sm">
          <div className="overline flex items-center gap-2"><Mail size={14}/> Account</div>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between"><dt style={{ color: "var(--elle-ink-dim)" }}>Email</dt><dd className="font-mono-elle">{author?.email}</dd></div>
            <div className="flex justify-between"><dt style={{ color: "var(--elle-ink-dim)" }}>Display name</dt><dd>{author?.display_name}</dd></div>
            <div className="flex justify-between"><dt style={{ color: "var(--elle-ink-dim)" }}><ShieldCheck size={12} className="inline mr-1"/> 2FA</dt><dd style={{ color: "var(--elle-amber)" }}>{author?.totp_enabled ? "Enabled" : "Not set"}</dd></div>
          </dl>
        </section>

        {/* Delivery */}
        <section className="elle-surface p-6 rounded-sm">
          <div className="overline flex items-center gap-2"><Clock size={14}/> Morning summary delivery</div>
          <p className="text-xs mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Daily email at the UTC time below, plus always visible on the dashboard where you can reply inline.
          </p>
          <div className="mt-3 p-3 rounded-sm text-xs"
               data-testid="resend-domain-notice"
               style={{ border: "1px solid var(--elle-oxblood)",
                        background: "rgba(163,50,50,0.08)",
                        color: "var(--elle-ink-dim)" }}>
            <strong style={{ color: "var(--elle-oxblood-bright)" }}>
              Resend is in testing mode.
            </strong>{" "}
            Until your sending domain is verified, Resend only delivers mail to the email
            address that created your Resend account ({" "}
            <span className="font-mono-elle">gabriellemstuart@gmail.com</span>){" "}
            and silently rejects everything else. To send to{" "}
            <span className="font-mono-elle">Elle@ellestuartauthor.com</span>, go to{" "}
            <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer"
               style={{ color: "var(--elle-amber)" }} className="underline">
              resend.com/domains
            </a>{" "}
            → Add Domain → <span className="font-mono-elle">ellestuartauthor.com</span>{" "}
            → copy the TXT/MX/DKIM records into your DNS. Typically verifies in 5–60 minutes.
            Once verified, this address will deliver immediately.
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <label className="overline block mb-2">Recipient email</label>
              <Input data-testid="settings-recipient-email" type="email"
                     value={pending.recipient_email}
                     onChange={(e)=>setPending(p=>({...p, recipient_email: e.target.value}))}/>
            </div>
            <div>
              <label className="overline block mb-2">Delivery time (UTC)</label>
              <Input data-testid="settings-summary-time" type="time"
                     value={pending.summary_time}
                     onChange={(e)=>setPending(p=>({...p, summary_time: e.target.value}))}/>
              <p className="text-xs mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                08:00 UTC = 08:00 GMT (winter) / 09:00 BST (British Summer).
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button data-testid="settings-save-delivery" disabled={!dirty}
                      onClick={()=>setSaveOpen(true)} className="btn-amber rounded-sm">
                Save (Tier A)
              </Button>
              <Button data-testid="settings-send-now" onClick={sendNow}
                      className="btn-ghost rounded-sm" variant="outline">
                <Send size={14} className="mr-2"/> Send test email now
              </Button>
            </div>

            {sendResult && !sendResult.sent && (
              <div className="mt-3 p-4 rounded-sm text-xs"
                   data-testid="send-failure-panel"
                   style={{ border: "1px solid var(--elle-oxblood)",
                            background: "rgba(163,50,50,0.12)",
                            color: "var(--elle-ink)" }}>
                <div className="overline mb-2" style={{ color: "var(--elle-oxblood-bright)" }}>
                  Send FAILED
                </div>
                <div className="font-mono-elle leading-relaxed whitespace-pre-wrap"
                     style={{ color: "var(--elle-ink-dim)" }}>
                  {sendResult.reason}
                </div>
                {/* Resend-testing-mode specific help */}
                {sendResult.reason && sendResult.reason.toLowerCase().includes("testing emails") && (
                  <div className="mt-3 pt-3"
                       style={{ borderTop: "1px solid var(--elle-line)",
                                color: "var(--elle-ink-dim)" }}>
                    <strong style={{ color: "var(--elle-amber-bright)" }}>
                      What this means:
                    </strong>{" "}
                    Your inbox <span className="font-mono-elle">Elle@ellestuartauthor.com</span>{" "}
                    exists and receives normal email fine — but Resend (your API email provider)
                    is in testing mode and will only dispatch to the exact address that owns
                    your Resend account until a domain is verified.
                    <div className="mt-2">
                      <strong style={{ color: "var(--elle-amber-bright)" }}>Fix:</strong>{" "}
                      <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer"
                         style={{ color: "var(--elle-amber)" }} className="underline">
                        resend.com/domains
                      </a> → Add Domain → <span className="font-mono-elle">ellestuartauthor.com</span>{" "}
                      → add the DNS records Resend gives you. Typically verifies in 5–60 min.
                    </div>
                    <div className="mt-2">
                      <strong style={{ color: "var(--elle-amber-bright)" }}>To get the test working tonight</strong>{" "}
                      without touching DNS: temporarily set the recipient to{" "}
                      <span className="font-mono-elle">gabriellemstuart@gmail.com</span>,
                      Save, click Send test email now — you'll receive it. Then switch back
                      once verified.
                    </div>
                  </div>
                )}
              </div>
            )}
            {sendResult && sendResult.sent && (
              <div className="mt-3 p-3 rounded-sm text-xs"
                   data-testid="send-success-panel"
                   style={{ border: "1px solid var(--elle-moss)",
                            background: "rgba(61,90,64,0.10)",
                            color: "var(--elle-ink)" }}>
                <div className="overline" style={{ color: "var(--elle-moss)" }}>
                  Sent
                </div>
                <div className="mt-1">Test summary delivered. Check your inbox within 1–2 min.</div>
              </div>
            )}
          </div>
        </section>

        {/* Tier A passphrase — forgot / reset */}
        <section className="elle-surface p-6 rounded-sm lg:col-span-2" data-testid="tier-a-section">
          <div className="overline flex items-center gap-2"><KeyRound size={14}/> Tier A passphrase</div>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
            The separate passphrase you set up for behaviour / voice / autonomy / discovery edits.
            Distinct from your dashboard login password.
            <span className="block mt-2" style={{ color: "var(--elle-ink-faint)" }}>
              If you've forgotten it, reset with your dashboard password + a current 2FA code.
            </span>
          </p>
          <Button data-testid="tier-a-reset-btn"
                  onClick={()=>setTarOpen(true)}
                  className="btn-amber rounded-sm mt-4">
            Reset Tier A passphrase
          </Button>
        </section>

        {/* Two-factor authentication */}
        <section className="elle-surface p-6 rounded-sm lg:col-span-2" data-testid="totp-section">
          <div className="overline flex items-center gap-2"><ShieldCheck size={14}/> Two-factor authentication (Tier B)</div>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
            Required for hard-limit changes and Tier B proposal approvals.
            {author?.totp_enabled ? (
              <span className="block mt-2 font-mono-elle" style={{ color: "var(--elle-amber)" }}>
                Status: enabled. If you never got to save the secret during setup, reveal it below and add it to an authenticator app (Google Authenticator, Authy, 1Password).
              </span>
            ) : (
              <span className="block mt-2" style={{ color: "var(--elle-oxblood-bright)" }}>
                Status: not enabled.
              </span>
            )}
          </p>

          {!totpInfo && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button data-testid="totp-reveal-btn"
                      onClick={()=>{ setTotpRotate(false); setTotpOpen(true); }}
                      className="btn-amber rounded-sm">
                Show my 2FA secret
              </Button>
              <Button data-testid="totp-rotate-btn"
                      onClick={()=>{ setTotpRotate(true); setTotpOpen(true); }}
                      variant="outline"
                      className="btn-ghost rounded-sm">
                Rotate to a new secret
              </Button>
            </div>
          )}

          {totpInfo && (
            <div className="mt-4" data-testid="totp-info-display">
              <div className="flex items-start gap-2 mb-3 text-xs" style={{ color: "var(--elle-amber)" }}>
                <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                <span>
                  {totpInfo.rotated
                    ? "This is a NEW secret. Your previous authenticator entry no longer works. Re-add this one."
                    : "Add this to an authenticator app. It stays the same between visits — you can return here if you lose it."}
                </span>
              </div>
              <div className="elle-surface-2 p-5 rounded-sm space-y-3">
                <div>
                  <div className="overline mb-1">Manual entry secret</div>
                  <div className="font-mono-elle text-lg tracking-widest break-all select-all"
                       data-testid="totp-secret-display"
                       style={{ color: "var(--elle-amber)", userSelect: "all" }}>
                    {totpInfo.secret}
                  </div>
                </div>
                <div>
                  <div className="overline mb-1">Or scan / paste URI</div>
                  <div className="font-mono-elle text-xs break-all select-all"
                       data-testid="totp-uri-display"
                       style={{ color: "var(--elle-ink-dim)", userSelect: "all" }}>
                    {totpInfo.uri}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button data-testid="totp-copy-btn" onClick={copyTotpSecret}
                        className="btn-ghost rounded-sm" variant="outline">
                  {totpCopied ? <><CheckCircle2 size={14} className="mr-2"/>Copied</> : <><Copy size={14} className="mr-2"/>Copy secret</>}
                </Button>
                <Button data-testid="totp-dismiss-btn" onClick={()=>setTotpInfo(null)}
                        className="btn-amber rounded-sm">
                  Done — it's in my authenticator
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Recovery */}
        <section className="elle-surface p-6 rounded-sm lg:col-span-2"
                 style={{ borderColor: "var(--elle-oxblood)" }}>
          <div className="overline flex items-center gap-2" style={{ color: "var(--elle-oxblood-bright)" }}>
            <Key size={14}/> Account recovery codes
          </div>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
            Ten single-use codes. If you lose your password or your 2FA device, use one to recover access from the
            login page. Generating a fresh set invalidates any existing codes.
            {settings && (
              <span className="block mt-2 font-mono-elle" style={{ color: "var(--elle-amber)" }}>
                Currently held: {settings.recovery_codes_count} unused code{settings.recovery_codes_count === 1 ? "" : "s"}.
              </span>
            )}
          </p>

          {!recoveryCodes && (
            <Button data-testid="generate-recovery-btn"
                    onClick={()=>setRgOpen(true)}
                    className="btn-oxblood rounded-sm mt-4">
              {settings?.recovery_codes_count > 0 ? "Generate fresh codes (invalidates existing)" : "Generate recovery codes"}
            </Button>
          )}

          {recoveryCodes && (
            <div className="mt-4" data-testid="recovery-codes-display">
              <div className="flex items-start gap-2 mb-3 text-xs"
                   style={{ color: "var(--elle-amber)" }}>
                <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                <span>
                  These will only be shown once. Save them to a password manager now.
                </span>
              </div>
              <div className="elle-surface-2 p-5 rounded-sm font-mono-elle text-lg tracking-wider grid grid-cols-2 gap-3 select-all"
                   style={{ color: "var(--elle-ink)", userSelect: "all" }}>
                {recoveryCodes.map((c) => (
                  <div key={c} className="flex items-center gap-2 select-all" style={{ userSelect: "all" }}>
                    <span style={{ color: "var(--elle-ink-faint)" }} className="text-xs">·</span>
                    <span style={{ userSelect: "all" }}>{c}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button data-testid="copy-codes-btn" onClick={copyCodes}
                        className="btn-ghost rounded-sm" variant="outline">
                  {copied ? <><CheckCircle2 size={14} className="mr-2"/>Copied</> : <><Copy size={14} className="mr-2"/>Copy all</>}
                </Button>
                <Button data-testid="download-codes-btn" onClick={downloadCodes}
                        className="btn-ghost rounded-sm" variant="outline">
                  <Download size={14} className="mr-2"/> Download .txt
                </Button>
                <Button data-testid="dismiss-codes-btn" onClick={()=>setRecoveryCodes(null)}
                        className="btn-amber rounded-sm">
                  I've saved them
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Discovery — observation-mode hashtags */}
        <section className="elle-surface p-6 rounded-sm lg:col-span-2" data-testid="discovery-section">
          <div className="overline flex items-center gap-2"><Compass size={14}/> Discovery · observation mode</div>
          <p className="text-xs mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
            The agent samples these Bluesky feeds every 15 minutes to learn what works.
            Metadata only — raw post content is never surfaced, just aggregates.
            {discovery?.summary && (
              <span className="block mt-2 font-mono-elle" style={{ color: "var(--elle-amber)" }}>
                Last 24h: {discovery.summary.total_observed} post(s) sampled across {(discovery.config?.hashtags || []).length} hashtag(s).
              </span>
            )}
          </p>
          {discovery && (
            <>
              <div className="mt-4 flex flex-wrap gap-2">
                {(discovery.config?.hashtags || []).map((t) => (
                  <div key={t} data-testid={`discovery-tag-${t}`}
                       className="flex items-center gap-2 rounded-sm text-xs font-mono-elle px-3 py-1.5"
                       style={{ border: "1px solid var(--elle-line)", color: "var(--elle-ink)" }}>
                    <span>{t}</span>
                    <button onClick={() => askTierA("discovery-remove", { tag: t }, `Remove ${t}`)}
                            className="hover:opacity-70"
                            data-testid={`discovery-remove-${t}`}>
                      <X size={12} style={{ color: "var(--elle-ink-dim)" }}/>
                    </button>
                  </div>
                ))}
                {(discovery.config?.hashtags || []).length === 0 && (
                  <div className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                    No hashtags — discovery won't sample until you add one.
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Input data-testid="discovery-new-tag" className="max-w-xs"
                       placeholder="#NewHashtag"
                       value={newHashtag}
                       onChange={(e)=>setNewHashtag(e.target.value)}/>
                <Button data-testid="discovery-add-tag"
                        className="btn-amber rounded-sm"
                        disabled={!newHashtag.trim()}
                        onClick={()=>askTierA("discovery-add", { tag: newHashtag.trim() },
                                              `Add ${newHashtag.trim()} to discovery`)}>
                  Add (Tier A)
                </Button>
              </div>
              {discovery.summary?.suggested_hashtags?.length > 0 && (
                <div className="mt-4 p-4 rounded-sm"
                     data-testid="discovery-suggestions-block"
                     style={{ border: "1px solid var(--elle-amber)",
                              background: "rgba(197,160,89,0.07)" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} style={{ color: "var(--elle-amber)" }}/>
                    <span className="overline" style={{ color: "var(--elle-amber-bright)" }}>
                      The agent suggests ({discovery.summary.suggested_hashtags.length})
                    </span>
                  </div>
                  <p className="text-xs mb-3" style={{ color: "var(--elle-ink-dim)" }}>
                    These hashtags show up repeatedly in your streams but aren&rsquo;t being sampled yet.
                    Click one to prefill the input above — then hit <em>Add (Tier A)</em> to start watching it.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {discovery.summary.suggested_hashtags.map((s) => (
                      <button key={s} data-testid={`discovery-suggest-${s}`}
                              onClick={()=>setNewHashtag(s)}
                              className="text-xs font-mono-elle px-3 py-1.5 rounded-sm hover:bg-[var(--elle-amber)] hover:text-[var(--elle-bg)] transition-colors"
                              style={{ border: "1px solid var(--elle-amber)",
                                       color: "var(--elle-amber-bright)" }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {/* Hashtag defaults — what pre-fills on every new Compose draft */}
        <section className="elle-surface p-6 rounded-sm lg:col-span-2"
                 data-testid="hashtag-defaults-section">
          <div className="overline flex items-center gap-2">
            <Hash size={14}/> Hashtag defaults · per platform
          </div>
          <p className="text-xs mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
            These are the hashtags that pre-fill every new Compose draft for each platform.
            One tag per entry (with or without <span className="font-mono-elle">#</span>) —
            separate with spaces. Edit freely; changes apply to the next draft you generate.
          </p>
          {hashtagDefaults && (
            <div className="mt-5 space-y-4">
              {["bluesky", "twitter", "facebook", "instagram", "tiktok"].map((p) => (
                <div key={p} data-testid={`hashtag-defaults-${p}`}>
                  <label className="overline block mb-1" style={{ color: "var(--elle-ink-dim)" }}>
                    {p}{" "}
                    <span className="font-mono-elle"
                          style={{ color: "var(--elle-ink-faint)" }}>
                      · {(hashtagInputs[p] || "").split(/\s+/).filter(Boolean).length} tag(s)
                    </span>
                  </label>
                  <Input
                    data-testid={`hashtag-input-${p}`}
                    value={hashtagInputs[p] || ""}
                    onChange={(e) => setHashtagInputs({ ...hashtagInputs, [p]: e.target.value })}
                    placeholder="#Fantasy #BookSky #WritingCommunity"
                    className="font-mono-elle text-sm"/>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2 flex-wrap">
                <Button
                  onClick={async () => {
                    setHashtagBusy(true);
                    try {
                      const payload = {};
                      for (const [p, str] of Object.entries(hashtagInputs)) {
                        payload[p] = (str || "")
                          .split(/[\s,]+/)
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .map((t) => (t.startsWith("#") ? t : "#" + t));
                      }
                      await api.put("/hashtags/defaults", { platforms: payload });
                      toast.success("Hashtag defaults saved.");
                      await loadHashtagDefaults();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Save failed.");
                    } finally {
                      setHashtagBusy(false);
                    }
                  }}
                  disabled={hashtagBusy}
                  data-testid="hashtag-defaults-save"
                  className="btn-amber rounded-sm">
                  {hashtagBusy ? "Saving…" : "Save defaults"}
                </Button>
                <button type="button"
                        onClick={loadHashtagDefaults}
                        disabled={hashtagBusy}
                        data-testid="hashtag-defaults-reset"
                        className="text-xs font-mono-elle underline hover:text-[var(--elle-amber)]"
                        style={{ color: "var(--elle-ink-dim)" }}>
                  reset to saved
                </button>
                <span className="text-xs font-mono-elle"
                      style={{ color: "var(--elle-ink-faint)" }}>
                  No passphrase needed — pre-fills only, every post still reviewed before publish.
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Autonomy — bounded follow/unfollow */}
        <section className="elle-surface p-6 rounded-sm lg:col-span-2"
                 style={{ borderColor: "var(--elle-moss)" }}
                 data-testid="autonomy-section">
          <div className="overline flex items-center justify-between">
            <span className="flex items-center gap-2"><Users size={14}/> Bounded autonomy · follow / unfollow</span>
            {autonomy && (
              <span className="font-mono-elle text-xs" style={{
                color: autonomy.paused ? "var(--elle-oxblood-bright)" : "var(--elle-moss)"
              }}>
                {autonomy.paused ? "PAUSED" : "ACTIVE"}
              </span>
            )}
          </div>
          <p className="text-xs mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
            Weekly job (Mondays 09:00 UTC). Follows up to{" "}
            <span className="font-mono-elle">{autonomy?.follow_cap_per_week ?? 3}</span>{" "}
            verified shortlist accounts per week. Auto-unfollows any handle with{" "}
            <span className="font-mono-elle">
              {autonomy?.unfollow_incident_min ?? 2}+ incidents / {autonomy?.unfollow_window_days ?? 30}d
            </span>{" "}
            (abuse log hits or Tier-3 flags).
          </p>

          {autonomy && (
            <>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button data-testid="autonomy-toggle-pause"
                        onClick={()=>askTierA("toggle-pause", { paused: !autonomy.paused },
                                              autonomy.paused ? "Resume autonomous follows" : "Pause autonomous follows")}
                        className={autonomy.paused ? "btn-amber rounded-sm" : "btn-oxblood rounded-sm"}>
                  {autonomy.paused ? "Resume autonomy (Tier A)" : "Pause autonomy (Tier A)"}
                </Button>
                <Button data-testid="autonomy-run-now"
                        onClick={runAutonomyNow} disabled={runningNow}
                        className="btn-ghost rounded-sm" variant="outline">
                  <Play size={14} className="mr-2"/>{runningNow ? "Running…" : "Run now"}
                </Button>
                <div className="flex items-center gap-2">
                  <label className="overline">Cap/wk</label>
                  <Input data-testid="autonomy-cap-input"
                         type="number" min={0} max={20}
                         className="w-20"
                         defaultValue={autonomy.follow_cap_per_week}
                         onBlur={(e) => {
                           const v = parseInt(e.target.value, 10);
                           if (Number.isFinite(v) && v !== autonomy.follow_cap_per_week) {
                             askTierA("set-cap", { cap: v }, `Set weekly cap to ${v}`);
                           }
                         }}/>
                </div>
              </div>

              {/* Shortlist table */}
              <div className="mt-5 elle-surface-2 rounded-sm overflow-hidden">
                <table className="w-full text-sm" data-testid="autonomy-shortlist">
                  <thead>
                    <tr className="text-left overline" style={{ color: "var(--elle-ink-dim)" }}>
                      <th className="p-3">Handle</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Verified</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(autonomy.shortlist || []).map((e) => (
                      <tr key={e.handle}
                          data-testid={`shortlist-row-${e.handle}`}
                          className="border-t" style={{ borderColor: "var(--elle-line)" }}>
                        <td className="p-3 align-top">
                          <div className="font-mono-elle" style={{ color: "var(--elle-ink)" }}>
                            @{e.handle}
                          </div>
                          {e.label && <div className="text-xs mt-0.5"
                                          style={{ color: "var(--elle-ink-dim)" }}>{e.label}</div>}
                          {e.note && <div className="text-xs mt-0.5"
                                          style={{ color: "var(--elle-ink-faint)" }}>{e.note}</div>}
                          {e.unfollow_reason && (
                            <div className="text-xs mt-1" style={{ color: "var(--elle-oxblood-bright)" }}>
                              {e.unfollow_reason}
                            </div>
                          )}
                        </td>
                        <td className="p-3 align-top font-mono-elle text-xs">
                          <span style={{
                            color: e.status === "followed" ? "var(--elle-moss)"
                                 : e.status === "unfollowed" ? "var(--elle-oxblood-bright)"
                                 : e.status === "error" || e.status === "unresolved" ? "var(--elle-oxblood-bright)"
                                 : "var(--elle-ink-dim)"
                          }}>{e.status || "pending"}</span>
                        </td>
                        <td className="p-3 align-top">
                          <button data-testid={`verify-toggle-${e.handle}`}
                                  onClick={()=>askTierA("verify",
                                    { handle: e.handle, verified: !e.verified },
                                    `${e.verified ? "Un-verify" : "Verify"} ${e.handle}`)}
                                  className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs font-mono-elle"
                                  style={{
                                    border: "1px solid var(--elle-line)",
                                    color: e.verified ? "var(--elle-moss)" : "var(--elle-ink-faint)"
                                  }}>
                            {e.verified ? <Check size={12}/> : <X size={12}/>}
                            {e.verified ? "verified" : "unverified"}
                          </button>
                        </td>
                        <td className="p-3 align-top text-right">
                          <Button size="sm" variant="outline" className="btn-ghost rounded-sm"
                                  data-testid={`remove-${e.handle}`}
                                  onClick={()=>askTierA("remove", { handle: e.handle },
                                                        `Remove ${e.handle} from shortlist`)}>
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {(autonomy.shortlist || []).length === 0 && (
                      <tr>
                        <td colSpan={4} className="p-6 text-center text-xs font-mono-elle"
                            style={{ color: "var(--elle-ink-faint)" }}>
                          Shortlist empty — add a handle below.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Add new handle */}
              <div className="mt-4 flex flex-wrap gap-2 items-end">
                <div>
                  <label className="overline block mb-1">Handle</label>
                  <Input data-testid="new-handle-input"
                         placeholder="someone.bsky.social"
                         value={newHandle}
                         onChange={(e)=>setNewHandle(e.target.value)}/>
                </div>
                <div>
                  <label className="overline block mb-1">Label (optional)</label>
                  <Input data-testid="new-handle-label"
                         placeholder="Author name / note"
                         value={newHandleLabel}
                         onChange={(e)=>setNewHandleLabel(e.target.value)}/>
                </div>
                <Button data-testid="add-handle-btn"
                        disabled={!newHandle.trim()}
                        onClick={()=>askTierA("add-handle",
                          { handle: newHandle.trim().replace(/^@/,""), label: newHandleLabel.trim() },
                          `Add ${newHandle} to shortlist`)}
                        className="btn-amber rounded-sm">
                  Add to shortlist (Tier A)
                </Button>
              </div>
            </>
          )}
        </section>

        <section className="elle-surface p-6 rounded-sm lg:col-span-2" data-testid="llm-keys-section">
          <div className="overline flex items-center gap-2">
            <Key size={14}/> LLM API keys (direct billing, skip Emergent credits)
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Paste your own Google Gemini and Anthropic Claude API keys to use direct billing
            for image generation and (later) text generation. Stored server-side and never
            echoed back to the browser — only the last 4 chars are shown to confirm.
            Google Gemini has a free tier of <strong>500 images/day</strong> on the 2.5 Flash
            Image model, which is plenty for daily Bluesky graphics and Substack headers.
          </p>

          <div className="mt-5 grid md:grid-cols-2 gap-5">
            <div>
              <label className="overline block mb-2 flex items-center justify-between">
                <span>Google Gemini API key</span>
                {llmCfg?.gemini_api_key?.set && (
                  <span className="text-xs font-mono-elle"
                        data-testid="gemini-key-mask"
                        style={{ color: "var(--elle-moss)" }}>
                    <Check size={10} className="inline -mt-0.5"/> set · ends …{llmCfg.gemini_api_key.last4}
                  </span>
                )}
              </label>
              <Input type="password"
                     data-testid="gemini-api-key"
                     value={llmForm.gemini_api_key}
                     onChange={(e)=>setLlmForm(f=>({...f, gemini_api_key:e.target.value}))}
                     placeholder={llmCfg?.gemini_api_key?.set ? "(leave blank to keep existing)" : "AIzaSy…"}
                     autoComplete="new-password"/>
              <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Get one at <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--elle-amber)]">aistudio.google.com/app/apikey</a> — free tier 500/day.
              </p>
            </div>

            <div>
              <label className="overline block mb-2 flex items-center justify-between">
                <span>Anthropic Claude API key</span>
                {llmCfg?.claude_api_key?.set && (
                  <span className="text-xs font-mono-elle"
                        data-testid="claude-key-mask"
                        style={{ color: "var(--elle-moss)" }}>
                    <Check size={10} className="inline -mt-0.5"/> set · ends …{llmCfg.claude_api_key.last4}
                  </span>
                )}
              </label>
              <Input type="password"
                     data-testid="claude-api-key"
                     value={llmForm.claude_api_key}
                     onChange={(e)=>setLlmForm(f=>({...f, claude_api_key:e.target.value}))}
                     placeholder={llmCfg?.claude_api_key?.set ? "(leave blank to keep existing)" : "sk-ant-…"}
                     autoComplete="new-password"/>
              <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Get one at <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--elle-amber)]">console.anthropic.com</a>. Optional — not yet wired, reserved for future direct-billing migration.
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Button onClick={saveLlmCfg} disabled={llmBusy}
                    data-testid="llm-save-btn"
                    className="btn-amber rounded-sm">
              {llmBusy ? "Saving…" : "Save LLM keys"}
            </Button>
            {llmCfg?.updated_at && (
              <span className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                Last updated: {new Date(llmCfg.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        </section>

        <section className="elle-surface p-6 rounded-sm lg:col-span-2" data-testid="meta-credentials-section">
          <div className="overline flex items-center gap-2">
            <Facebook size={14}/><Instagram size={14}/> Meta (Facebook / Instagram) credentials
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Paste your Meta Developer App credentials here. Secret fields are stored server-side
            and never echoed back to the browser — only the last 4 characters are shown so you
            can verify. Leave a secret field blank to keep the existing value.
          </p>

          <div className="mt-5 grid md:grid-cols-2 gap-5">
            {/* App ID — public */}
            <div>
              <label className="overline block mb-2">App ID</label>
              <Input data-testid="meta-app-id"
                     value={metaForm.app_id}
                     onChange={(e)=>setMetaForm(f=>({...f, app_id:e.target.value}))}
                     placeholder="e.g. 1451871499951963"/>
              <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Dashboard → App settings → Basic
              </p>
            </div>

            {/* App Secret — stored, never returned */}
            <div>
              <label className="overline block mb-2 flex items-center justify-between">
                <span>App Secret</span>
                {metaCfg?.app_secret?.set && (
                  <span className="text-xs font-mono-elle"
                        data-testid="meta-app-secret-mask"
                        style={{ color: "var(--elle-moss)" }}>
                    <Check size={10} className="inline -mt-0.5"/> set · ends …{metaCfg.app_secret.last4}
                  </span>
                )}
              </label>
              <Input type="password"
                     data-testid="meta-app-secret"
                     value={metaForm.app_secret}
                     onChange={(e)=>setMetaForm(f=>({...f, app_secret:e.target.value}))}
                     placeholder={metaCfg?.app_secret?.set ? "(leave blank to keep existing)" : "Paste the new secret here"}
                     autoComplete="new-password"/>
              <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Dashboard → App settings → Basic → Show (rotate if compromised)
              </p>
            </div>

            {/* Page ID */}
            <div>
              <label className="overline block mb-2">Facebook Page ID</label>
              <Input data-testid="meta-page-id"
                     value={metaForm.page_id}
                     onChange={(e)=>setMetaForm(f=>({...f, page_id:e.target.value}))}
                     placeholder="e.g. 1234567890"/>
              <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Your author Page → About → Page ID (bottom of page)
              </p>
            </div>

            {/* IG Business Account ID */}
            <div>
              <label className="overline block mb-2">Instagram Business Account ID</label>
              <Input data-testid="meta-ig-business-id"
                     value={metaForm.ig_business_account_id}
                     onChange={(e)=>setMetaForm(f=>({...f, ig_business_account_id:e.target.value}))}
                     placeholder="e.g. 17841400000000000"/>
              <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Graph API Explorer: GET /me/accounts?fields=instagram_business_account
              </p>
            </div>

            {/* Page Access Token — long-lived, treated as a secret */}
            <div className="md:col-span-2">
              <label className="overline block mb-2 flex items-center justify-between">
                <span>Page Access Token (long-lived)</span>
                {metaCfg?.page_access_token?.set && (
                  <span className="text-xs font-mono-elle"
                        data-testid="meta-token-mask"
                        style={{ color: "var(--elle-moss)" }}>
                    <Check size={10} className="inline -mt-0.5"/> set · ends …{metaCfg.page_access_token.last4}
                  </span>
                )}
              </label>
              <Input type="password"
                     data-testid="meta-page-access-token"
                     value={metaForm.page_access_token}
                     onChange={(e)=>setMetaForm(f=>({...f, page_access_token:e.target.value}))}
                     placeholder={metaCfg?.page_access_token?.set ? "(leave blank to keep existing)" : "Paste the long-lived Page Access Token"}
                     autoComplete="new-password"/>
              <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Generated via Graph API Explorer → Get Page Access Token → exchange for long-lived via /oauth/access_token?grant_type=fb_exchange_token
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Button onClick={saveMetaCfg} disabled={metaBusy}
                    data-testid="meta-save-btn"
                    className="btn-amber rounded-sm">
              {metaBusy ? "Saving…" : "Save Meta credentials"}
            </Button>
            {metaCfg?.updated_at && (
              <span className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                Last updated: {new Date(metaCfg.updated_at).toLocaleString()}
              </span>
            )}
          </div>

          <div className="mt-5 p-4 rounded-sm text-xs"
               style={{ border: "1px solid var(--elle-amber)",
                        background: "rgba(197,160,89,0.06)",
                        color: "var(--elle-ink-dim)" }}>
            <strong style={{ color: "var(--elle-amber-bright)" }}>Getting the Page Access Token:</strong>
            <br/>
            1. Open <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="underline">Graph API Explorer</a>
            <br/>
            2. Select your app (Meta dropdown top-right) · Get User Access Token · pick scopes:
            <span className="font-mono-elle"> pages_manage_posts, pages_read_engagement, pages_manage_engagement, instagram_basic, instagram_content_publish, instagram_manage_comments</span>
            <br/>
            3. GET <span className="font-mono-elle">/me/accounts</span> — grab the <span className="font-mono-elle">access_token</span> for your author Page
            <br/>
            4. That's short-lived (1 hour). Exchange for long-lived (60 days):
            <br/>
            <span className="font-mono-elle block mt-1">curl "https://graph.facebook.com/v20.0/oauth/access_token?grant_type=fb_exchange_token&client_id=APP_ID&client_secret=APP_SECRET&fb_exchange_token=SHORT_LIVED"</span>
            <br/>
            5. Paste the long-lived token into the field above.
          </div>
        </section>

        <section className="elle-surface p-6 rounded-sm lg:col-span-2">
          <div className="overline flex items-center gap-2"><KeyRound size={14}/> Demo helpers</div>
          <p className="text-xs mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Simulate the feel of real incoming content while platforms remain mocked.
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Simulate incoming reply</label>
              <Input data-testid="settings-handle" value={handle} onChange={(e)=>setHandle(e.target.value)} placeholder="@handle"/>
              <Textarea data-testid="settings-text" rows={3} className="mt-2"
                        value={sampleText} onChange={(e)=>setSampleText(e.target.value)}
                        placeholder="Try: 'The prose drags in chapter 12.' or something abusive."/>
              <Button data-testid="settings-simulate" onClick={runFilter} className="btn-amber rounded-sm mt-3">
                Run through Abuse Filter
              </Button>
            </div>
            <div>
              <label className="overline block mb-2">Trend agent</label>
              <p className="text-xs" style={{ color: "var(--elle-ink-dim)" }}>
                Draft a sample behavioural proposal from a fictional observation.
              </p>
              <Button data-testid="settings-propose" onClick={makeProposal}
                      className="btn-ghost rounded-sm mt-3" variant="outline">
                Draft a sample proposal
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Tier A save dialog for delivery */}
      <TierApprovalDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        tier="A"
        title="Save delivery settings"
        description="These settings govern the morning email. Tier A passphrase required."
        onConfirm={async (passphrase) => { await saveSettings(passphrase); }}
      />

      {/* Tier A dialog for any autonomy / discovery change */}
      <TierApprovalDialog
        open={!!autonomyAction}
        onOpenChange={(o) => { if (!o) setAutonomyAction(null); }}
        tier="A"
        title={autonomyAction?.title || "Confirm change"}
        description="Changes to autonomy or discovery are Tier A. Confirm with your passphrase."
        onConfirm={async (passphrase) => {
          await runAutonomy(passphrase, autonomyAction);
          setAutonomyAction(null);
        }}
      />

      {/* Tier A reset dialog */}
      {tarOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(11,14,20,0.8)" }}>
          <div className="elle-surface p-6 rounded-sm max-w-md w-full"
               style={{ borderColor: "var(--elle-amber)" }}>
            <div className="overline mb-2" style={{ color: "var(--elle-amber)" }}>
              Reset Tier A passphrase
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--elle-ink-dim)" }}>
              Dashboard password + current 2FA code required. The old passphrase is overwritten.
            </p>
            <div className="space-y-3">
              <div>
                <label className="overline block mb-1">Dashboard password</label>
                <Input data-testid="tar-password" type="password" autoFocus
                       value={tarPwd} onChange={(e)=>setTarPwd(e.target.value)}/>
              </div>
              <div>
                <label className="overline block mb-1">6-digit 2FA code</label>
                <Input data-testid="tar-totp" inputMode="numeric" maxLength={6}
                       className="font-mono-elle tracking-widest"
                       placeholder="· · · · · ·"
                       value={tarTotp}
                       onChange={(e)=>setTarTotp(e.target.value.replace(/\D/g,""))}/>
              </div>
              <div>
                <label className="overline block mb-1">New Tier A passphrase</label>
                <Input data-testid="tar-new" type="password" minLength={6}
                       placeholder="At least 6 characters"
                       value={tarNew} onChange={(e)=>setTarNew(e.target.value)}/>
                <p className="text-xs mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                  Make this deliberately memorable — you'll need it for behaviour changes.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" className="btn-ghost rounded-sm"
                      onClick={()=>{ setTarOpen(false); setTarPwd(""); setTarTotp(""); setTarNew(""); }}>
                Cancel
              </Button>
              <Button data-testid="tar-confirm-btn"
                      onClick={resetTierA}
                      disabled={tarBusy || !tarPwd || tarTotp.length !== 6 || tarNew.length < 6}
                      className="btn-amber rounded-sm">
                {tarBusy ? "Resetting…" : "Reset passphrase"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* TOTP reveal/rotate — password prompt */}
      {totpOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(11,14,20,0.8)" }}>
          <div className="elle-surface p-6 rounded-sm max-w-md w-full"
               style={{ borderColor: totpRotate ? "var(--elle-oxblood)" : "var(--elle-amber)" }}>
            <div className="overline mb-2"
                 style={{ color: totpRotate ? "var(--elle-oxblood-bright)" : "var(--elle-amber)" }}>
              {totpRotate ? "Rotate 2FA — password required" : "Show 2FA secret — password required"}
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--elle-ink-dim)" }}>
              {totpRotate
                ? "This issues a new secret and invalidates the previous authenticator entry. Only do this if you think the old one was compromised."
                : "This re-displays your existing secret so you can add it to an authenticator app. Nothing changes on the server."}
            </p>
            <Input data-testid="totp-password-input" type="password"
                   value={totpPwd} onChange={(e)=>setTotpPwd(e.target.value)}
                   placeholder="Your dashboard password" autoFocus
                   onKeyDown={(e) => { if (e.key === "Enter" && totpPwd) revealTotp(); }}/>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="btn-ghost rounded-sm"
                      onClick={()=>{ setTotpOpen(false); setTotpPwd(""); }}>
                Cancel
              </Button>
              <Button data-testid="totp-confirm-btn"
                      onClick={revealTotp} disabled={totpBusy || !totpPwd}
                      className={totpRotate ? "btn-oxblood rounded-sm" : "btn-amber rounded-sm"}>
                {totpBusy ? "…" : (totpRotate ? "Rotate" : "Show secret")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recovery gen dialog — password prompt */}
      {rgOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(11,14,20,0.8)" }}>
          <div className="elle-surface p-6 rounded-sm max-w-md w-full"
               style={{ borderColor: "var(--elle-oxblood)" }}>
            <div className="overline mb-2" style={{ color: "var(--elle-oxblood-bright)" }}>
              Confirm password to generate codes
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--elle-ink-dim)" }}>
              This will invalidate any existing recovery codes.
            </p>
            <Input data-testid="recovery-password" type="password"
                   value={pwd} onChange={(e)=>setPwd(e.target.value)}
                   placeholder="Your dashboard password" autoFocus/>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="btn-ghost rounded-sm"
                      onClick={()=>{ setRgOpen(false); setPwd(""); }}>
                Cancel
              </Button>
              <Button data-testid="recovery-confirm-btn"
                      onClick={generateCodes} disabled={rgBusy || !pwd}
                      className="btn-oxblood rounded-sm">
                {rgBusy ? "Generating…" : "Generate 10 codes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
